import { useCallback, useEffect, useMemo, useState } from "react";
import { FaEye, FaEyeSlash, FaTrash, FaWifi } from "react-icons/fa";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { isMobileAkanthasAccount } from "./mobileModuleUtils";
import InstallationMetricSnapshot from "./installation/InstallationMetricSnapshot";
import { InstallationPickerSheet } from "./installation/InstallationPickerSheet";
import InstallationSubpageLayout from "./installation/InstallationSubpageLayout";
import {
  INSTALLATION_DETAIL_STALE_KEY,
  INSTALLATION_LIST_STALE_KEY,
} from "./installation/installationConfigUtils";
import { writeAzureConfig } from "./installation/installationApi";
import {
  areWifiDraftsEqual,
  buildWifiCredentialsAzurePartial,
  buildWifiCredentialsClearPartial,
  clearWifiSavedAt,
  formatWifiSavedAt,
  getPrimaryWifiCredential,
  hasExistingSsidInConfig,
  hasWifiCredentialsInConfig,
  readWifiSavedAt,
  validateWifiCredentialDraft,
  writeWifiSavedAt,
} from "./installation/installationWifiUtils";
import { useInstallationCamera } from "./installation/useInstallationCamera";

const AppInstallationWifiConfig = ({ setCookies, saveUser }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const { loading, configData, setConfigData, api, token, reload } =
    useInstallationCamera(id);

  const [savedDraft, setSavedDraft] = useState({ ssid: "", password: "" });
  const [ssid, setSsid] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [savedAt, setSavedAt] = useState(() => readWifiSavedAt(id));

  useEffect(() => {
    if (!isMobileAkanthasAccount()) {
      history.replace(`/app/installationdetails/${id}`);
    }
  }, [history, id]);

  useEffect(() => {
    const primary = getPrimaryWifiCredential(configData);
    const nextDraft = {
      ssid: primary?.ssid ?? "",
      password: "",
    };
    setSavedDraft(nextDraft);
    setSsid(nextDraft.ssid);
    setPassword("");
  }, [configData]);

  useEffect(() => {
    setSavedAt(readWifiSavedAt(id));
  }, [id]);

  const draft = useMemo(() => ({ ssid, password }), [ssid, password]);
  const isDirty = !areWifiDraftsEqual(draft, savedDraft);
  const hasConfiguredWifi = hasWifiCredentialsInConfig(configData);
  const configuredSsid = hasConfiguredWifi
    ? getPrimaryWifiCredential(configData)?.ssid ?? ""
    : "";
  const showExistingSsidWarning =
    Boolean(configuredSsid.trim()) &&
    Boolean(ssid.trim()) &&
    hasExistingSsidInConfig(configData, ssid) &&
    isDirty;

  const dismiss = () => {
    setSsid(savedDraft.ssid);
    setPassword(savedDraft.password);
    history.goBack();
  };

  const handleSave = useCallback(async () => {
    const validation = validateWifiCredentialDraft(draft);
    if (!validation.valid) {
      toast.error(t(validation.errorKey));
      return;
    }

    if (!isDirty || saving) {
      return;
    }

    if (showExistingSsidWarning) {
      toast.error(t("installation_wifi_existing_ssid_warning"));
      return;
    }

    setSaving(true);
    try {
      const partial = buildWifiCredentialsAzurePartial(ssid, password);
      const { output } = await writeAzureConfig(
        api,
        token,
        id,
        configData,
        partial,
      );
      const savedTime = new Date();
      writeWifiSavedAt(id, savedTime);
      setSavedAt(savedTime);
      setConfigData(output);
      setSavedDraft({ ssid: String(ssid).trim(), password: "" });
      setPassword("");
      sessionStorage.setItem(INSTALLATION_LIST_STALE_KEY, "1");
      sessionStorage.setItem(INSTALLATION_DETAIL_STALE_KEY, "1");
      toast.success(t("installation_wifi_saved_toast"));
      await reload({ silent: true });
      history.goBack();
    } catch {
      toast.error(t("installation_save_failed"));
    } finally {
      setSaving(false);
    }
  }, [
    api,
    configData,
    draft,
    history,
    id,
    isDirty,
    password,
    reload,
    saving,
    setConfigData,
    showExistingSsidWarning,
    ssid,
    t,
    token,
  ]);

  const savedAtLabel = formatWifiSavedAt(t, savedAt, i18n.language);

  const handleClearConfig = useCallback(async () => {
    if (saving || !hasConfiguredWifi) {
      return false;
    }

    setSaving(true);
    try {
      const { output } = await writeAzureConfig(
        api,
        token,
        id,
        configData,
        buildWifiCredentialsClearPartial(),
      );
      clearWifiSavedAt(id);
      setSavedAt(null);
      setConfigData(output);
      setSavedDraft({ ssid: "", password: "" });
      setSsid("");
      setPassword("");
      sessionStorage.setItem(INSTALLATION_LIST_STALE_KEY, "1");
      sessionStorage.setItem(INSTALLATION_DETAIL_STALE_KEY, "1");
      toast.success(t("installation_wifi_cleared_toast"));
      await reload({ silent: true });
      return true;
    } catch {
      toast.error(t("installation_save_failed"));
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    api,
    configData,
    hasConfiguredWifi,
    id,
    reload,
    saving,
    setConfigData,
    t,
    token,
  ]);

  const headerAction = isDirty ? (
    <button
      type="button"
      className="installation-subpage-nav-button installation-subpage-nav-button-save"
      onClick={() => void handleSave()}
      disabled={saving || loading}
    >
      {saving ? t("installation_saving") : t("installation_save")}
    </button>
  ) : null;

  return (
    <InstallationSubpageLayout
      pageTitle={t("installation_wifi_page_title")}
      onBack={dismiss}
      setCookies={setCookies}
      saveUser={saveUser}
      headerAction={headerAction}
    >
      <p className="installation-subpage-footnote installation-wifi-page-hint">
        {t("installation_wifi_page_hint")}
      </p>

      {configuredSsid && (
        <InstallationMetricSnapshot
          icon={FaWifi}
          iconVariant="network"
          eyebrow={t("installation_wifi_configured_eyebrow")}
          value={configuredSsid}
          valueLoading={loading}
        />
      )}

      {hasConfiguredWifi && savedAtLabel && (
        <p className="installation-subpage-footnote installation-wifi-saved-line">
          {savedAtLabel}
        </p>
      )}

      <section className="installation-subpage-card installation-wifi-form-card">
        <label className="installation-field-row installation-wifi-field">
          <span>{t("installation_wifi_ssid_label")}</span>
          <input
            type="text"
            className="installation-wifi-input"
            value={ssid}
            onChange={(event) => setSsid(event.target.value)}
            placeholder={t("installation_wifi_ssid_placeholder")}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            disabled={saving}
          />
        </label>

        <label className="installation-field-row installation-wifi-field">
          <span>{t("installation_wifi_password_label")}</span>
          <div className="installation-wifi-password-wrap">
            <input
              type={showPassword ? "text" : "password"}
              className="installation-wifi-input installation-wifi-input-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder={t("installation_wifi_password_placeholder")}
              autoComplete="new-password"
              disabled={saving}
            />
            <button
              type="button"
              className="installation-wifi-password-toggle"
              onClick={() => setShowPassword((current) => !current)}
              disabled={saving}
              aria-label={
                showPassword
                  ? t("installation_wifi_password_hide")
                  : t("installation_wifi_password_show")
              }
            >
              {showPassword ? (
                <FaEyeSlash aria-hidden />
              ) : (
                <FaEye aria-hidden />
              )}
            </button>
          </div>
        </label>

        {showExistingSsidWarning && (
          <p className="installation-wifi-warning" role="status">
            {t("installation_wifi_existing_ssid_warning")}
          </p>
        )}

        {hasConfiguredWifi && (
          <button
            type="button"
            className="installation-wifi-clear-button"
            onClick={() => setClearConfirmOpen(true)}
            disabled={saving || loading}
          >
            <FaTrash aria-hidden />
            {t("installation_wifi_clear_action")}
          </button>
        )}
      </section>

      <InstallationPickerSheet
        open={clearConfirmOpen}
        title={t("installation_wifi_clear_confirm_title")}
        onDismiss={() => {
          if (!saving) {
            setClearConfirmOpen(false);
          }
        }}
        onSave={async () => {
          const cleared = await handleClearConfig();
          if (cleared) {
            setClearConfirmOpen(false);
          }
        }}
        saveLabel={t("installation_wifi_clear_confirm_action")}
        dirty
        saving={saving}
      >
        <div className="installation-picker-confirm-content">
          <p className="installation-picker-confirm-body">
            {t("installation_wifi_clear_confirm_body")}
          </p>
        </div>
      </InstallationPickerSheet>
    </InstallationSubpageLayout>
  );
};

export default AppInstallationWifiConfig;
